/**
 * InstallAppPrompt — simple, non-technical "Install NoFee app" banner.
 *
 * iter91. Designed for non-technical customers — no "Add to Home
 * Screen" jargon. Single visible CTA: "Install NoFee app". Behaviour:
 *
 *   Android / Chrome / Edge: listens for `beforeinstallprompt`, stashes
 *     the event, and triggers the native install UI on click.
 *
 *   iOS Safari: Apple does NOT expose a programmatic install hook, so
 *     after the user taps the CTA we open a sheet that visually shows
 *     a finger pointing at the Share button and the one-line
 *     instruction "Tap and choose Add". No multi-step tutorial.
 *
 *   Already-installed (standalone): never renders.
 *   Mobile only: hidden on tablets and desktop where install is rare.
 *   Dismissed: stays hidden for 30 days via localStorage.
 */
import React, { useEffect, useState } from 'react';
import { X, Share as ShareIcon, Smartphone, Plus, MoreVertical } from 'lucide-react';

const DISMISS_KEY = 'nofee_pwa_dismissed_at';
const DISMISS_DAYS = 30;

const isStandalone = () =>
  (typeof window !== 'undefined') && (
    window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator.standalone === true
  );

const isIOS = () =>
  typeof navigator !== 'undefined'
  && /iPad|iPhone|iPod/.test(navigator.userAgent)
  && !window.MSStream;

const isMobile = () =>
  typeof navigator !== 'undefined'
  && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const recentlyDismissed = () => {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    return ts && (Date.now() - ts) < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch { return false; }
};

const InstallAppPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  // iter150 — `manualSheet` (previously `iosSheet`) opens the 2-step
  // fallback guide for BOTH iOS Safari and any Android browser that
  // doesn't emit `beforeinstallprompt` (Firefox mobile, Samsung Internet,
  // Chrome without engagement heuristics, etc.). Content inside the sheet
  // is platform-specific via `isIOS()` check at render time.
  const [manualSheet, setManualSheet] = useState(false);

  useEffect(() => {
    if (isStandalone() || !isMobile() || recentlyDismissed()) return undefined;

    // Android / Chrome path — fires when the browser deems the site installable.
    // We stash the event so the button can call native `prompt()` on click.
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // For iOS AND Android-without-beforeinstallprompt we still want the
    // banner visible so the user can trigger the manual fallback. Small
    // delay so the banner doesn't slam in before the first paint.
    const t = setTimeout(() => setShow(true), 1500);
    return () => {
      clearTimeout(t);
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const onInstallClick = async () => {
    // Preferred path: native browser install prompt (Chrome / Edge Android).
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === 'accepted') {
        setShow(false);
      }
      setDeferredPrompt(null);
      return;
    }
    // Fallback: no native prompt available → open the 2-step manual guide.
    // Content adapts to iOS vs Android automatically at render.
    setManualSheet(true);
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setShow(false);
    setManualSheet(false);
  };

  if (!show) return null;

  return (
    <>
      {/* Bottom-anchored install banner */}
      <div
        data-testid="pwa-install-banner"
        className="fixed bottom-3 left-3 right-3 z-[999] md:hidden"
        role="dialog"
        aria-label="Install NoFee"
      >
        <div className="rounded-2xl border border-gold-500/40 bg-zinc-950/95 backdrop-blur-md shadow-[0_20px_60px_-15px_rgba(0,0,0,0.7)] px-4 py-3 flex items-center gap-3">
          <img
            src="/brand/nofee_logo_mark.png"
            alt=""
            className="w-11 h-11 rounded-xl bg-black object-contain flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="text-[16px] font-bold text-white tracking-tight">Install NoFee</div>
          </div>
          <button
            type="button"
            onClick={onInstallClick}
            data-testid="pwa-install-btn"
            className="shrink-0 px-3.5 py-2 rounded-lg bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black text-[12.5px] font-extrabold transition-all"
          >
            Install
          </button>
          <button
            type="button"
            onClick={dismiss}
            data-testid="pwa-install-dismiss"
            className="shrink-0 w-7 h-7 rounded-lg text-white/45 hover:text-white/85 hover:bg-white/5 flex items-center justify-center transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Fallback instruction sheet — iter149/150: minimal 2-step popup,
          big icons, no filler text. Content adapts to iOS (Safari Share
          → Add to Home Screen) vs Android (⋮ menu → Install app). */}
      {manualSheet && (
        <div
          data-testid="pwa-install-sheet"
          className="fixed inset-0 z-[1000] bg-black/65 backdrop-blur-sm flex items-end md:hidden"
          onClick={dismiss}
        >
          <div
            className="w-full bg-zinc-950 rounded-t-3xl border-t border-gold-500/40 p-6 pb-9 animate-in slide-in-from-bottom"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto w-12 h-1.5 rounded-full bg-white/15 mb-6" />
            <div className="flex items-center gap-3 mb-6">
              <img src="/brand/nofee_logo_mark.png" alt="" className="w-12 h-12 rounded-xl" />
              <div>
                <h2 className="text-[17px] font-bold text-white tracking-tight">Install NoFee</h2>
                <p className="text-[12.5px] text-white/55">2 quick taps on {isIOS() ? 'iPhone' : 'Android'}</p>
              </div>
            </div>

            {isIOS() ? (
              <ol className="space-y-4">
                <li className="flex items-center gap-4 p-4 rounded-2xl bg-white/[0.04] border border-white/10">
                  <div className="w-14 h-14 rounded-2xl bg-sky-500/15 border border-sky-500/40 flex items-center justify-center shrink-0">
                    <ShareIcon className="w-7 h-7 text-sky-300" strokeWidth={2} />
                  </div>
                  <div className="flex-1">
                    <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/45">Step 1</div>
                    <p className="text-[15px] font-bold text-white mt-0.5">Tap Share</p>
                  </div>
                </li>
                <li className="flex items-center gap-4 p-4 rounded-2xl bg-white/[0.04] border border-white/10">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center shrink-0">
                    <Plus className="w-7 h-7 text-emerald-300" strokeWidth={2.2} />
                  </div>
                  <div className="flex-1">
                    <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/45">Step 2</div>
                    <p className="text-[15px] font-bold text-white mt-0.5">Add to Home Screen</p>
                  </div>
                </li>
              </ol>
            ) : (
              <ol className="space-y-4" data-testid="pwa-install-sheet-android">
                <li className="flex items-center gap-4 p-4 rounded-2xl bg-white/[0.04] border border-white/10">
                  <div className="w-14 h-14 rounded-2xl bg-sky-500/15 border border-sky-500/40 flex items-center justify-center shrink-0">
                    <MoreVertical className="w-7 h-7 text-sky-300" strokeWidth={2.2} />
                  </div>
                  <div className="flex-1">
                    <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/45">Step 1</div>
                    <p className="text-[15px] font-bold text-white mt-0.5">Tap the menu</p>
                  </div>
                </li>
                <li className="flex items-center gap-4 p-4 rounded-2xl bg-white/[0.04] border border-white/10">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center shrink-0">
                    <Smartphone className="w-7 h-7 text-emerald-300" strokeWidth={2} />
                  </div>
                  <div className="flex-1">
                    <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/45">Step 2</div>
                    <p className="text-[15px] font-bold text-white mt-0.5">Tap Install app</p>
                  </div>
                </li>
              </ol>
            )}

            <button
              type="button"
              onClick={dismiss}
              data-testid="pwa-install-got-it"
              className="mt-7 w-full py-3 rounded-xl text-white/60 hover:text-white/90 font-medium text-[13px] transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallAppPrompt;
